/**
 * CMPメインロジック（初期化・イベント調整）
 *
 * Clipkit.cmpの公開APIを提供し、各モジュールを統合する。
 * DOMContentLoadedで<script type="application/json" id="cmp-config">から設定を読み取り、
 * 自動的に初期化する。
 */

import { validateConfig, resolveRegion } from './config';
import {
  readConsent,
  writeConsent,
  clearConsentKeepGeo,
  readGeo,
  writeGeo,
  isGeoFresh,
  generateUUID,
  resetMemoryFallback
} from './cookie';
import { resolveTexts } from './i18n';
import { sendConsentLog, fetchRegion } from './api';
import { injectStyles, createBanner, createPreferencesModal, createRevisitButton } from './ui';
import { getShadowRoot, destroyShadowRoot } from './shadow-root';
import { processTemplates, startObserver, stopObserver } from './element-manager';
import { pushGtmConsentUpdateIfEnabled, buildAllGrantedCategories } from './gtm-consent-mode';

// 内部状態
let _config = null;
let _texts = null;
let _bannerEl = null;
let _modalEl = null;
let _revisitEl = null;
let _focusBeforeModal = null;
let _initialized = false;
let _displayMode = 'normal';
let _defaultConsentCategories = null;

/**
 * 初期化
 *
 * リージョン判定がCookieで完結する場合（geoが新鮮 or regionUrl未設定の旧形式）は
 * 同期的に初期化を完了する。サーバー問い合わせが必要な場合のみPromiseを返す。
 *
 * @param {Object} rawConfig - パース済みの設定オブジェクト
 * @returns {undefined|Promise<void>}
 */
function run(rawConfig) {
  const { valid, config, errors } = validateConfig(rawConfig);

  if (!valid) {
    errors.forEach((e) => console.error('[CMP]', e));
    // 必須カテゴリのtemplateのみアクティベート
    activateRequiredOnly(rawConfig);
    return;
  }

  // 国別設定（default以外のキー）が1つもなければ、どの国でもdefault適用で挙動が同一のため
  // リージョン判定自体が不要。fetchRegionを呼ばずに即初期化する
  // （regionSettingsが空のサイトが大多数のため、判定APIへの不要なトラフィックを防ぐ）
  if (!hasCountrySettings(config.regionSettings)) {
    initWithCountry(config, null);
    return;
  }

  // リージョン判定: Cookieのgeoが新鮮ならそのまま使用
  // （country=nullの「判定不能」キャッシュもTTL内は有効。毎ページロードの再判定を避ける）
  const geo = readGeo(config.cookie.name);
  if (geo && isGeoFresh(geo)) {
    initWithCountry(config, geo.country || null);
    return;
  }

  // regionUrl未設定（旧形式）ならサーバー問い合わせせずcountry=nullで進行
  if (!config.regionUrl) {
    initWithCountry(config, null);
    return;
  }

  // geoなし/TTL切れ → サーバーに問い合わせ
  // タイムアウト後に遅れて判定が完了した場合もCookieに保存し、次回ページロードから利用する
  // （低速回線のユーザーが毎ページロードでタイムアウト待ちを繰り返すのを防ぐ）
  return fetchRegion(config.regionUrl, undefined, ({ country }) => {
    writeGeo(config.cookie.name, country, config.cookie);
  })
    .then(({ country, resolved }) => {
      if (resolved) {
        // 判定完了（countryがnullでも「判定done」）→ Cookieに保存
        writeGeo(config.cookie.name, country, config.cookie);
      }
      // タイムアウト/失敗時はCookieを更新せずcountry=nullとして進行（次回再判定）
      initWithCountry(config, country);
    })
    .catch((e) => {
      // 初期化中の例外をunhandled rejectionにせず、同期パスのエラー時と同様にフォールバック
      console.error('[CMP]', e);
      activateRequiredOnly(rawConfig);
    });
}

/**
 * regionSettingsにdefault以外の国別キーが存在するか
 * @param {Object} regionSettings - validateConfig済みのregionSettings
 * @returns {boolean}
 */
function hasCountrySettings(regionSettings) {
  if (!regionSettings || typeof regionSettings !== 'object') return false;
  return Object.keys(regionSettings).some((key) => key !== 'default');
}

/**
 * リージョン解決後の初期化本体
 * @param {Object} config - バリデーション済み設定
 * @param {string|null} country - 解決済み国コード
 */
function initWithCountry(config, country) {
  const region = resolveRegion(config.regionSettings, country);

  if (region.displayMode === 'hidden_no_cmp') {
    // 「CMPが存在しない場合と同じ状態にする」モード:
    // バナー/モーダル/リビジットボタンは非表示、同意ログなし、既存同意Cookieも無視。
    // _configを設定しないため公開API（acceptAll等）もrun()前と同様にno-opになる。
    //
    // ただしCMPが無いかのように振る舞う以上、著者がdata-cmp-categoryでゲートした
    // コンテンツ（script/埋め込み等）は展開されなければならない（CMPが無ければ
    // そもそもゲート自体が存在せず全て動作するため）。全カテゴリgrantedとして
    // 既存templateを展開し、以降に動的追加されるtemplateもobserverで展開する。
    // これはGTM側の全granted update（下記）と対称。
    const grantAll = () => true;
    processTemplates(grantAll);
    startObserver(grantAll);

    // GTM Consent Mode有効時のみ、<head>のdefault(denied)を全grantedのupdateで解除する。
    // headのdefault scriptは地域非依存で全訪問者に出力されるため、解除しないと
    // このリージョンではGTM管理タグが永久にdeniedのまま（「タグ側のデフォルト挙動に
    // 委ねる」というモードの趣旨 = CMPが存在しない場合と同じ状態にする）。
    pushGtmConsentUpdateIfEnabled(config, buildAllGrantedCategories(config.categories));
    return;
  }

  _config = config;
  _texts = resolveTexts(config.translations, config.defaultLanguage);
  _displayMode = region.displayMode;
  _defaultConsentCategories = region.defaultConsentCategories;

  injectStyles(getShadowRoot(), region.buttonStyles);

  let consent = readConsent(config.cookie.name);

  if (consent && needsReConsent(consent, config.categories) && region.displayMode === 'hidden_default_consent') {
    // 陳腐化した同意（カテゴリ変更等）はバナーで再同意を取れないモードのため無視し、
    // 「同意なし」に統一する（getConsent/モーダル初期チェックとtemplate展開の状態を一貫させる）。
    // geoフィールドは保持したまま古い同意のみCookieから削除する
    clearConsentKeepGeo(config.cookie.name, config.cookie);
    consent = null;
  }

  if (consent && !needsReConsent(consent, config.categories)) {
    // 同意済み — templateを展開してイベント発火（hidden_default_consentでも既存同意を尊重）
    applyConsent(consent.categories);
    // <head>のinline updateで既に発火済みだが、万一失敗していた場合の保険として再送
    pushGtmConsentUpdateIfEnabled(_config, consent.categories);
    dispatchConsentEvent('initial', consent.categories);
    showRevisitButton();
  } else if (region.displayMode === 'hidden_default_consent') {
    // バナーを描画せず全カテゴリをデフォルト同意として扱う
    // （ユーザー操作なしのためCookie保存・CmpConsentLog記録はしない）
    applyDefaultConsent();
  } else {
    // 未同意またはカテゴリ変更 — バナー表示
    showBanner();
  }

  // data-cmp-action のdocumentレベルイベント委譲（重複登録防止）
  if (!_initialized) {
    document.addEventListener('click', handleDataAction);
    _initialized = true;
  }
}

/**
 * hidden_default_consentモードのデフォルト同意適用
 * 全カテゴリgrantedでtemplate展開・GTM送信し、リビジットボタンのみ表示する
 */
function applyDefaultConsent() {
  const categories = buildAllGrantedCategories(_config.categories);

  applyConsent(categories);

  // GTM Consent Mode有効サイトのみ全カテゴリgrantedを送信
  pushGtmConsentUpdateIfEnabled(_config, categories);

  // 同意済みパスと同様にinitialイベントを発火する
  // （cmp:consentイベント駆動のサイト独自連携がこのモードでも起動できるように）
  dispatchConsentEvent('initial', categories);

  // リビジットボタンから詳細モーダルを開ける（初期チェックは全カテゴリON）
  showRevisitButton();
}

/**
 * カテゴリ変更検知（再同意が必要か判定）
 * Cookie内のcategoriesキーと設定のcategoriesキーを比較する
 */
function needsReConsent(consent, categories) {
  const cookieKeys = Object.keys(consent.categories).sort();
  const configKeys = categories.map((c) => c.key).sort();
  // カテゴリの追加・削除を検知
  if (cookieKeys.join(',') !== configKeys.join(',')) return true;
  // required属性の変更を検知（GDPR: 目的変更時は再同意が必要）
  return categories.some((cat) => cat.required && !consent.categories[cat.key]);
}

/**
 * 同意を適用する（template展開 + MutationObserver開始）
 */
function applyConsent(categories) {
  const checker = (category) => Boolean(categories[category]);
  processTemplates(checker);
  startObserver(checker);
}

/**
 * 初期化エラー時 — 必須カテゴリのtemplateのみアクティベート
 */
function activateRequiredOnly(rawConfig) {
  if (!rawConfig || !Array.isArray(rawConfig.categories)) return;
  const requiredKeys = rawConfig.categories.filter((c) => c && c.required).map((c) => c.key);
  const checker = (category) => requiredKeys.includes(category);
  processTemplates(checker);
  startObserver(checker);
}

/**
 * 同意バナーを表示する
 */
function showBanner() {
  if (_bannerEl) return;

  _bannerEl = createBanner(_texts, {
    onAcceptAll: () => acceptAll(),
    onAcceptNecessary: () => acceptNecessary(),
    onShowPreferences: () => showPreferences()
  });

  getShadowRoot().appendChild(_bannerEl);

  // バナーコンテナにフォーカス（特定ボタンへの誘導を避けGDPR中立性を確保）
  _bannerEl.focus();
}

/**
 * 同意バナーを非表示にする
 */
function hideBanner() {
  if (_bannerEl) {
    _bannerEl.remove();
    _bannerEl = null;
  }
}

/**
 * リビジットボタンを表示する
 */
function showRevisitButton() {
  if (_revisitEl) return;

  _revisitEl = createRevisitButton(() => showPreferences(), _texts);
  getShadowRoot().appendChild(_revisitEl);
}

/**
 * リビジットボタンを非表示にする
 */
function hideRevisitButton() {
  if (_revisitEl) {
    _revisitEl.remove();
    _revisitEl = null;
  }
}

/**
 * 設定モーダルを表示する
 */
function showPreferences() {
  if (!_config) return;
  if (_modalEl) return;

  hideRevisitButton();
  // shadow 内要素にフォーカスがある場合 document.activeElement はホストを指すため
  // shadow root の activeElement を優先する（light DOM トリガ時は null → document へフォールバック）
  _focusBeforeModal = getShadowRoot().activeElement || document.activeElement;

  const consent = readConsent(_config.cookie.name);
  const currentCategories = {};
  _config.categories.forEach((cat) => {
    if (consent && consent.categories) {
      currentCategories[cat.key] = cat.required ? true : Boolean(consent.categories[cat.key]);
    } else if (_displayMode === 'hidden_default_consent') {
      // デフォルト同意モード — 初期チェックは全カテゴリON
      currentCategories[cat.key] = true;
    } else {
      // 未同意 — リージョン解決結果のdefault_consent_categoriesをフォールバックに使用
      // （requiredカテゴリは常にON）
      currentCategories[cat.key] =
        cat.required || Boolean(_defaultConsentCategories && _defaultConsentCategories.includes(cat.key));
    }
  });

  const modalCallbacks = {
    onSave: (selections) => savePreferences(selections),
    onAcceptAll: () => acceptAll(),
    onAcceptNecessary: () => acceptNecessary(),
    onClose: () => closePreferencesModal()
  };
  // 同意済み、またはデフォルト同意で動作中（hidden_default_consent・Cookieなし）の場合に
  // 撤回ボタンを表示する（管理画面の説明でも撤回ボタンの表示を明言している）
  if (consent || _displayMode === 'hidden_default_consent') {
    modalCallbacks.onRevoke = () => reset();
  }

  _modalEl = createPreferencesModal(_texts, _config.categories, currentCategories, modalCallbacks);

  getShadowRoot().appendChild(_modalEl);

  // モーダル内の最初のフォーカス可能要素にフォーカス
  const firstFocusable = _modalEl.querySelector('button');
  if (firstFocusable) firstFocusable.focus();
}

/**
 * 設定モーダルを閉じる（同意状態は変更しない）
 */
function closePreferencesModal() {
  if (_modalEl) {
    _modalEl.remove();
    _modalEl = null;
  }
  // モーダルを開いた要素にフォーカスを戻す
  if (_focusBeforeModal) {
    _focusBeforeModal.focus();
    _focusBeforeModal = null;
  }
  // バナーが表示されていない場合のみリビジットボタンを再表示
  if (!_bannerEl) {
    const consent = _config ? readConsent(_config.cookie.name) : null;
    // hidden_default_consentでは未同意でもリビジットボタンを常時表示する
    if (consent || (_config && _displayMode === 'hidden_default_consent')) {
      showRevisitButton();
    }
  }
}

/**
 * 全カテゴリ許可
 */
function acceptAll() {
  if (!_config) return;
  const categories = {};
  _config.categories.forEach((cat) => {
    categories[cat.key] = true;
  });
  commitConsent(categories, determineAction());
}

/**
 * 必須のみ許可
 */
function acceptNecessary() {
  if (!_config) return;
  const categories = {};
  _config.categories.forEach((cat) => {
    categories[cat.key] = cat.required;
  });
  commitConsent(categories, determineAction());
}

/**
 * 個別カテゴリの同意を保存
 * @param {Object} selections - { analytics: true, ... }
 */
function savePreferences(selections) {
  if (!_config) return;
  const categories = {};
  _config.categories.forEach((cat) => {
    categories[cat.key] = cat.required ? true : Boolean(selections[cat.key]);
  });
  commitConsent(categories, determineAction());
}

/**
 * アクションタイプを判定する（initial / update）
 */
function determineAction() {
  const existing = readConsent(_config.cookie.name);
  return existing ? 'update' : 'initial';
}

/**
 * 同意を確定する（Cookie保存・ログ送信・UI更新・template展開・イベント発火）
 */
function commitConsent(categories, action) {
  const existing = readConsent(_config.cookie.name);
  const consent = {
    id: existing && existing.id ? existing.id : generateUUID(),
    categories,
    timestamp: new Date().toISOString()
  };

  writeConsent(_config.cookie.name, consent, _config.cookie);

  sendConsentLog(_config.consentLogUrl, {
    consent_id: consent.id,
    categories: consent.categories,
    action
  });

  hideBanner();
  closePreferencesModal();
  applyConsent(categories);

  pushGtmConsentUpdateIfEnabled(_config, categories);

  dispatchConsentEvent(action, categories);
}

/**
 * 同意をリセットする（Cookie削除 → revokeログ送信 → バナー再表示）
 */
function reset() {
  if (!_config) return;

  const consent = readConsent(_config.cookie.name);
  if (consent) {
    sendConsentLog(_config.consentLogUrl, {
      consent_id: consent.id,
      categories: consent.categories,
      action: 'revoke'
    });
  }

  // 同意のみ削除し、リージョン判定キャッシュ（geo）は保持する
  clearConsentKeepGeo(_config.cookie.name, _config.cookie);
  stopObserver();

  const revokedCategories = {};
  if (_config.categories) {
    _config.categories.forEach((cat) => {
      revokedCategories[cat.key] = cat.required;
    });
  }
  pushGtmConsentUpdateIfEnabled(_config, revokedCategories);
  dispatchConsentEvent('revoke', revokedCategories);

  closePreferencesModal();
  hideRevisitButton();

  // 撤回はデフォルト同意からの明示的な離脱なので、hidden_default_consentでも
  // 再同意を取るためにバナーを表示する（「バナー非表示」はあくまで初期状態の挙動で、
  // ユーザーが自ら撤回した場合は除く）。撤回でCookieは削除済みのため、次回ページ
  // ロードでは再びデフォルト同意が適用され、バナーは出ない＝モードの趣旨は保たれる
  showBanner();
}

/**
 * 現在の同意状態を取得する
 * @returns {Object|null}
 */
function getConsent() {
  if (!_config) return null;
  return readConsent(_config.cookie.name);
}

/**
 * 特定カテゴリの同意状態を取得する
 * @param {string} category
 * @returns {boolean}
 */
function hasConsent(category) {
  const consent = getConsent();
  if (!consent || !consent.categories) return false;
  return Boolean(consent.categories[category]);
}

/**
 * カスタムイベントを発火する
 * @param {string} action - "initial" | "update" | "revoke"
 * @param {Object} categories
 */
function dispatchConsentEvent(action, categories) {
  document.dispatchEvent(
    new CustomEvent('cmp:consent', {
      detail: { action, categories }
    })
  );
}

/**
 * data-cmp-action属性のクリックイベントハンドラ（documentレベル委譲）
 */
function handleDataAction(e) {
  const el = e.target.closest('[data-cmp-action]');
  if (!el) return;

  const action = el.getAttribute('data-cmp-action');
  if (action === 'show-preferences') {
    e.preventDefault();
    showPreferences();
  }
}

/**
 * 内部状態を完全リセットする（テスト用）
 * @private
 */
function _destroy() {
  hideBanner();
  closePreferencesModal();
  hideRevisitButton();
  stopObserver();
  resetMemoryFallback();
  if (_initialized) {
    document.removeEventListener('click', handleDataAction);
    _initialized = false;
  }
  destroyShadowRoot();
  _config = null;
  _texts = null;
  _displayMode = 'normal';
  _defaultConsentCategories = null;
}

// 公開API
export default {
  run,
  showPreferences,
  acceptAll,
  acceptNecessary,
  savePreferences,
  getConsent,
  hasConsent,
  reset,
  _destroy
};
